import * as React from 'react';
import { useState, useEffect } from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { useDebounce } from '../../../../hooks/useDebounce';

/**
 * A TextField that debounces its onChange callback by 300ms.
 * Used across sub-option and sub-question rows to avoid excessive
 * re-renders / state updates while the user is typing.
 */
const DebouncedTextField: React.FC<Omit<TextFieldProps, 'onChange'> & { onChange: (value: string) => void }> = React.memo(({ value, onChange, ...props }) => {
    const [localValue, setLocalValue] = useState<string>((value as string) || '');
    const debouncedOnChange = useDebounce(onChange, 300);

    useEffect(() => {
        setLocalValue((value as string) || '');
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setLocalValue(newValue);
        debouncedOnChange(newValue);
    };

    return <TextField {...props} value={localValue} onChange={handleChange} />;
});

DebouncedTextField.displayName = 'DebouncedTextField';

export default DebouncedTextField;
